import { useState, useEffect, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';

// Known EMR verb vocabulary (v47); merged with any custom keys found live.
const KNOWN_VERBS = ['chart_read','notes_write','notes_sign','notes_cosign','protected_tabs','discharge',
  'change_loc','treatment_plan','groups_manage','groups_notes','outcome_measures','outcome_review_sign',
  'med_orders','med_orders_view','med_orders_review','mar','med_inventory','controlled_counts',
  'lab_requisitions','lab_results_view','lab_results_sign','intake_consents','rounds','rounds_compliance',
  'admits_transfers','ur_manage','ur_view','ur_concurrent','admit','manage_clinical_users',
  'manage_messenger_groups',
  'casefile_export','facesheet','discharge_tab','reports_view'];

const KNOWN_WIDGETS = ['missed_calls','my_queue','unclaimed_leads','log_referral','team_admits_gauge','my_admits',
  'admits_today','pending_admits','jts_admits','aca_totals','leaderboard','my_score','leads_action','callbacks_today',
  'vobs_outstanding','lead_sources','census_by_facility','open_beds','discharges_upcoming','ur_expiring',
  'collections_range','forecast_week','ai_review_pending','tickets_open','prescreens_requested',
  'clinical_safety','money_ar','ur_expiring_dollars','staffing_today','ready_to_bill'];
const SCOPES = [['my_day','Day'],['my_team','Team'],['organization','Org']];

const PROTECTED = ['ceo','admin'];

const s = {
  wrap: { padding: 32, maxWidth: 1200, margin: '0 auto', fontFamily: "'Inter', system-ui, sans-serif" },
  title: { margin: '0 0 4px', fontSize: 24, color: '#336090' },
  sub: { margin: '0 0 20px', color: '#666', fontSize: 14 },
  layout: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  rail: { width: 250, flexShrink: 0 },
  roleCard: (sel, prot) => ({ padding: '10px 14px', borderRadius: 10, marginBottom: 6, cursor: 'pointer',
    background: sel ? '#0878E8' : 'white', color: sel ? 'white' : prot ? '#9ca3af' : '#1a2a3a',
    border: '1px solid ' + (sel ? '#0878E8' : '#e5e7eb'), fontSize: 13.5, fontWeight: 600,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }),
  panel: { flex: 1, background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: 22 },
  secHead: { fontSize: 13, fontWeight: 800, color: '#336090', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '18px 0 10px' },
  chip: (on) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 14,
    fontSize: 12, fontWeight: 700, cursor: 'pointer', marginRight: 6, marginBottom: 6,
    border: '1px solid ' + (on ? '#bbf7d0' : '#e5e7eb'), background: on ? '#f0fdf4' : '#f8fafc',
    color: on ? '#166534' : '#9ca3af', userSelect: 'none' }),
  exceptTag: { fontSize: 10, fontWeight: 800, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 8 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, width: '100%', boxSizing: 'border-box' },
  saveBtn: { background: '#0878E8', color: 'white', border: 'none', padding: '10px 22px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  banner: (ok) => ({ background: ok ? '#f0fdf4' : '#fef2f2', border: '1px solid ' + (ok ? '#bbf7d0' : '#fca5a5'),
    color: ok ? '#166534' : '#991b1b', padding: '10px 14px', borderRadius: 10, fontSize: 13.5, marginBottom: 14 }),
  hint: { fontSize: 12, color: '#999', marginTop: 4 },
};

// One verb row: toggle + optional facility-exception picker
function VerbChip({ verb, value, facilities, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const on = value === true || (value && typeof value === 'object');
  const exceptIds = (value && typeof value === 'object' && Array.isArray(value.except_facilities)) ? value.except_facilities : [];
  const toggle = () => onChange(on ? undefined : true);
  const toggleFacility = (fid) => {
    const next = exceptIds.includes(fid) ? exceptIds.filter(x => x !== fid) : [...exceptIds, fid];
    onChange(next.length ? { except_facilities: next } : true);
  };
  return (
    <span style={{ display: 'inline-block' }}>
      <span style={s.chip(on)}>
        <span onClick={toggle}>{verb}</span>
        {on && (
          <span onClick={() => setPickerOpen(o => !o)} title="Facility exceptions" style={{ cursor: 'pointer' }}>
            {exceptIds.length ? <span style={s.exceptTag}>EXCEPT {exceptIds.length}</span> : <span style={{ opacity: 0.5 }}>@</span>}
          </span>
        )}
      </span>
      {on && pickerOpen && (
        <span style={{ display: 'block', margin: '2px 0 8px', padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12 }}>
          <strong>Granted everywhere EXCEPT:</strong>
          {facilities.map(f => (
            <label key={f.id} style={{ display: 'block', padding: '3px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={exceptIds.includes(f.id)} onChange={() => toggleFacility(f.id)} />
              {' '}{f.name}
            </label>
          ))}
        </span>
      )}
    </span>
  );
}

export default function Roles({ user }) {
  const token = user?.token || localStorage.getItem('tcio_token');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const [roles, setRoles] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dup, setDup] = useState({ role_key: '', display_name: '' });

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/roles`, { headers: H });
      const j = await r.json();
      if (j.ok) setRoles(j.roles.filter(x => x.active));
      const f = await fetch(`${API}/api/facilities`, { headers: H });
      const fj = await f.json();
      setFacilities((fj.data || fj || []).map(x => ({ id: x.id, name: x.display_name || x.org_name || x.name })));
    } catch (e) { console.error(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const pick = (r) => { setSel(r.role_key); setDraft({ ...r, tabs: [...(r.tabs || [])], emr_permissions: { ...(r.emr_permissions || {}) }, home_widgets: { ...(r.home_widgets || {}) } }); setMsg(null); };
  const allWidgets = Array.from(new Set([...KNOWN_WIDGETS, ...roles.flatMap(r => Object.keys(r.home_widgets || {}))])).sort();
  const toggleWidget = (w) => setDraft(d => {
    const hw = { ...d.home_widgets };
    if (hw[w]) delete hw[w]; else hw[w] = ['my_day'];
    return { ...d, home_widgets: hw };
  });
  const toggleScope = (w, sc) => setDraft(d => {
    const hw = { ...d.home_widgets };
    const cur = Array.isArray(hw[w]) ? hw[w] : ['my_day'];
    const next = cur.includes(sc) ? cur.filter(x => x !== sc) : [...cur, sc];
    hw[w] = next.length ? next : ['my_day'];
    return { ...d, home_widgets: hw };
  });

  // Tab vocabulary = union of tabs in use across all roles
  const allTabs = Array.from(new Set(roles.flatMap(r => r.tabs || []))).sort();
  // Verb vocabulary = known list merged with any custom keys live
  const allVerbs = Array.from(new Set([...KNOWN_VERBS, ...roles.flatMap(r => Object.keys(r.emr_permissions || {}))])).filter(v => v !== 'superuser').sort();

  const isProt = draft && PROTECTED.includes(draft.role_key);

  const toggleTab = (t) => setDraft(d => ({ ...d, tabs: d.tabs.includes(t) ? d.tabs.filter(x => x !== t) : [...d.tabs, t] }));
  const setVerb = (v, val) => setDraft(d => {
    const p = { ...d.emr_permissions };
    if (val === undefined) delete p[v]; else p[v] = val;
    return { ...d, emr_permissions: p };
  });

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/roles/${draft.role_key}`, { method: 'PATCH', headers: H,
        body: JSON.stringify({ tabs: draft.tabs, emr_permissions: draft.emr_permissions, display_name: draft.display_name, description: draft.description, home_widgets: draft.home_widgets }) });
      const j = await r.json();
      if (j.ok) { setMsg({ ok: true, text: 'Saved. Users on this role get the change at their next login.' }); load(); }
      else setMsg({ ok: false, text: j.error || 'Save failed' });
    } catch (e) { setMsg({ ok: false, text: 'Connection error' }); }
    setSaving(false);
  };

  const duplicate = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/roles`, { method: 'POST', headers: H,
        body: JSON.stringify({ role_key: dup.role_key, display_name: dup.display_name, clone_from: sel }) });
      const j = await r.json();
      if (j.ok) { setMsg({ ok: true, text: `Role ${j.role_key} created from ${sel}. It is immediately assignable on the Users page.` }); setDupOpen(false); setDup({ role_key: '', display_name: '' }); load(); }
      else setMsg({ ok: false, text: j.error || 'Create failed' });
    } catch (e) { setMsg({ ok: false, text: 'Connection error' }); }
    setSaving(false);
  };

  return (
    <div style={s.wrap}>
      <h2 style={s.title}>Roles</h2>
      <p style={s.sub}>What each role can see (tabs) and do (EMR permissions). Changes apply at each user's next login. CEO and Admin are superuser roles and cannot be edited.</p>
      <div style={s.layout}>
        <div style={s.rail}>
          {roles.map(r => (
            <div key={r.role_key} style={s.roleCard(sel === r.role_key, PROTECTED.includes(r.role_key))} onClick={() => pick(r)}>
              {r.display_name || r.role_key}
              <div style={{ fontSize: 10.5, fontWeight: 500, opacity: 0.7 }}>{r.role_key}{PROTECTED.includes(r.role_key) ? ' - superuser' : ''}</div>
            </div>
          ))}
        </div>
        <div style={s.panel}>
          {!draft ? (
            <div style={{ color: '#999', fontSize: 14 }}>Select a role to view or edit.</div>
          ) : (
            <div>
              {msg && <div style={s.banner(msg.ok)}>{msg.text}</div>}
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#666' }}>Display name</label>
                  <input style={s.input} value={draft.display_name || ''} disabled={isProt}
                    onChange={e => setDraft({ ...draft, display_name: e.target.value })} />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#666' }}>Description</label>
                  <input style={s.input} value={draft.description || ''} disabled={isProt}
                    onChange={e => setDraft({ ...draft, description: e.target.value })} />
                </div>
              </div>

              {isProt ? (
                <div style={{ ...s.banner(true), marginTop: 16 }}>Superuser role: sees everything, all permissions, always. Not editable by design.</div>
              ) : (
                <div>
                  <div style={s.secHead}>Tabs (what they see in the sidebar)</div>
                  {allTabs.map(t => (
                    <span key={t} style={s.chip(draft.tabs.includes(t))} onClick={() => toggleTab(t)}>{t}</span>
                  ))}

                  <div style={s.secHead}>EMR permissions (what they can do inside charts)</div>
                  <div style={s.hint}>Click a verb to grant/revoke. Click @ on a granted verb to carve out facilities where it does NOT apply.</div>
                  <div style={{ marginTop: 8 }}>
                    {allVerbs.map(v => (
                      <VerbChip key={v} verb={v} value={draft.emr_permissions[v]} facilities={facilities}
                        onChange={(val) => setVerb(v, val)} />
                    ))}
                  </div>

                  <div style={s.secHead}>Home widgets (what their Home dashboard shows)</div>
                  <div style={s.hint}>Click a widget to grant/revoke. Scope tags control which Home views it appears in (Day = personal, Team = supervisor, Org = executive). Empty = role falls back to the platform default set.</div>
                  <div style={{ marginTop: 8 }}>
                    {allWidgets.map(w => {
                      const on = !!(draft.home_widgets || {})[w];
                      const scopes = Array.isArray((draft.home_widgets || {})[w]) ? draft.home_widgets[w] : [];
                      return (
                        <span key={w} style={{ display: 'inline-block' }}>
                          <span style={s.chip(on)}>
                            <span onClick={() => toggleWidget(w)}>{w}</span>
                            {on && SCOPES.map(([sc, lbl]) => (
                              <span key={sc} onClick={() => toggleScope(w, sc)}
                                style={{ fontSize: 9.5, fontWeight: 800, padding: '1px 5px', borderRadius: 6, cursor: 'pointer',
                                  background: scopes.includes(sc) ? '#0878E8' : '#e5e7eb',
                                  color: scopes.includes(sc) ? 'white' : '#9ca3af' }}>{lbl}</span>
                            ))}
                          </span>
                        </span>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: 22, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button style={s.saveBtn} disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save Role'}</button>
                    <button style={{ ...s.saveBtn, background: 'white', color: '#336090', border: '1px solid #d1d5db' }}
                      onClick={() => setDupOpen(o => !o)}>Duplicate as new role</button>
                  </div>
                  {dupOpen && (
                    <div style={{ marginTop: 12, padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <input style={s.input} placeholder="role_key (e.g. weekend_supervisor)" value={dup.role_key}
                          onChange={e => setDup({ ...dup, role_key: e.target.value })} />
                        <input style={s.input} placeholder="Display name" value={dup.display_name}
                          onChange={e => setDup({ ...dup, display_name: e.target.value })} />
                        <button style={s.saveBtn} disabled={saving || !dup.role_key || !dup.display_name} onClick={duplicate}>Create</button>
                      </div>
                      <div style={s.hint}>Clones {sel}'s tabs and permissions as a starting point. Edit after creating.</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
